import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/survey-core.min.css';
import surveyTheme from '../surveyTheme';
import api from '../api/api';

/**
 * SurveyModal — renders a SurveyJS survey inside a modal overlay.
 *
 * Props:
 *   surveyData   { SurveyID, Title, SurveyJSON }  — SurveyJSON is already a parsed object
 *   onComplete   (surveyId) => void               — called after successful submission
 *   onDismiss    () => void
 *   dismissible  bool (default true)
 */
export default function SurveyModal({ surveyData, onComplete, onDismiss, dismissible = true }) {
    const [submitting, setSubmitting] = useState(false);

    // Build the SurveyJS Model once — never recreate it on re-renders
    const [model] = useState(() => {
        const m = new Model(surveyData.SurveyJSON);
        // Brand theme (light) — the modal panel itself is hardcoded light
        m.applyTheme(surveyTheme);

        m.onComplete.add(async (sender) => {
            setSubmitting(true);
            try {
                await api.post(`/survey/${surveyData.SurveyID}/respond`, {
                    response_data: sender.data,
                });
            } catch (err) {
                // Non-blocking — don't prevent the user from continuing
                console.error('Survey submit error:', err);
            } finally {
                setSubmitting(false);
                onComplete(surveyData.SurveyID);
            }
        });

        return m;
    });

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
                onClick={dismissible ? onDismiss : undefined}
            />

            {/* Modal panel */}
            <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                    <h3 className="text-xl font-bold text-slate-900">{surveyData.Title}</h3>
                    {dismissible && (
                        <button
                            onClick={onDismiss}
                            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                            aria-label="Close survey"
                        >
                            <X className="w-5 h-5 text-slate-500" />
                        </button>
                    )}
                </div>

                {/* Survey content */}
                <div className="overflow-y-auto p-4">
                    {submitting ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                        </div>
                    ) : (
                        <Survey model={model} />
                    )}
                </div>
            </div>
        </div>
    );
}
